import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faBan, faBox, faCalendar, faFileExcel, faSearch, faSync, faTractor, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { AggregateQueryCellInfoEventArgs, AggregateRowModel, ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import * as moment from 'moment-timezone';
import { finalize } from 'rxjs/operators';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { InventoryOnHandReport } from '../../models/inventory-on-hand-reports';
import { InventoryOnHandReportService } from '../../services/inventory-on-hand-report.service';
import { KW_QUANTITY_FORMAT } from '../../../../../@core/utils/ejs-constants';

const MOBILE_PAGE_SIZE = 25;
const GRAND_TOTAL_LABEL = 'Grand total:';

interface OnHandRow extends InventoryOnHandReport {
  dateReceived: string;
  bornOnDate: string;
}

interface StyleWithValue {
  value: string | number;
}

@Component({
  selector: 'inventory-on-hand-report',
  templateUrl: './inventory-on-hand-report.component.html',
  styleUrls: ['./inventory-on-hand-report.component.scss']
})
export class InventoryOnHandReportComponent implements OnInit {
  @ViewChild('grid') public grid: GridComponent;

  public faBan = faBan;
  public faBox = faBox;
  public faCalendar = faCalendar;
  public faFileExcel = faFileExcel;
  public faSearch = faSearch;
  public faSync = faSync;
  public faTractor = faTractor;
  public faWarehouse = faWarehouse;

  public isLoading = false;
  public searchTerm = '';
  public filterCount = 0;
  public rows: OnHandRow[] = [];
  private allRows: OnHandRow[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public pageSettings: PageSettingsModel = { pageSize: 200, pageSizes: [50, 100, 200, 500] };
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };
  public aggregates: AggregateRowModel[] = [{
    columns: [
      { type: 'Count', field: 'STOCK_LOCATOR', footerTemplate: GRAND_TOTAL_LABEL },
      { type: 'Sum', field: 'ONHAND_QUANTITY', format: KW_QUANTITY_FORMAT, footerTemplate: '${Sum}' }
    ]
  }];

  public columns: ColumnModel[] = [
    { field: 'STOCK_LOCATOR', headerText: 'Stock Locator', autoFit: true, clipMode: 'EllipsisWithTooltip' },
    { field: 'ITEM_DESCRIPTION', headerText: 'Item Description', autoFit: true, clipMode: 'EllipsisWithTooltip' },
    { field: 'GROWER', headerText: 'Grower', width: 250, clipMode: 'EllipsisWithTooltip' },
    { field: 'LOT_NUMBER', headerText: 'Lot #', autoFit: true },
    { field: 'dateReceived', headerText: 'Date Received', autoFit: true },
    { field: 'bornOnDate', headerText: 'BOD', autoFit: true },
    { field: 'CERT_ROOM', headerText: 'CERT RM', autoFit: true },
    { field: 'POOL_CODE', headerText: 'Pool', autoFit: true },
    { field: 'ITEM_NUMBER', headerText: 'Item Number', autoFit: true },
    { field: '', headerText: 'SQ', width: 80, allowFiltering: false, allowSorting: false },
    { field: 'TREATMENT', headerText: 'Treatment', autoFit: true },
    { field: 'LOT_QUALIFICATION', headerText: 'Qual', autoFit: true },
    { field: 'QC_NOTES', headerText: 'QC Notes', width: 200, clipMode: 'EllipsisWithTooltip' },
    { field: 'ONHAND_QUANTITY', headerText: 'Qty', autoFit: true, format: KW_QUANTITY_FORMAT, textAlign: 'Right' }
  ];

  private destroyRef = inject(DestroyRef);

  constructor(
    private inventoryOnHandReportService: InventoryOnHandReportService,
    private toastrService: NbToastrService,
    private errorService: ErrorService
  ) { }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.isLoading = true;
    this.inventoryOnHandReportService.getAllInventoryOnHandReportsFromSteamilt()
      .pipe(finalize(() => this.isLoading = false), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.allRows = (data ?? []).map(rec => this.toRow(rec));
          this.applySearch();
        },
        error: err => this.errorService.handleError(err)
      });
  }

  onSyncInventory() {
    this.isLoading = true;
    this.inventoryOnHandReportService.syncInventory()
      .pipe(finalize(() => this.isLoading = false), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastrService.success('Inventory sync completed successfully', 'Success');
          this.loadData();
        },
        error: () => this.toastrService.danger('Failed to sync inventory', 'Error')
      });
  }

  applySearch() {
    const term = this.searchTerm.trim().toLowerCase();
    this.rows = term
      ? this.allRows.filter(row => [row.STOCK_LOCATOR, row.ITEM_DESCRIPTION, row.GROWER, row.LOT_NUMBER, row.ITEM_NUMBER, row.POOL_CODE]
        .some(value => value?.toLowerCase().includes(term)))
      : [...this.allRows];
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  clearFilters() {
    this.grid?.clearFiltering();
    this.searchTerm = '';
    this.applySearch();
    this.filterCount = 0;
  }

  onActionComplete(args: { requestType?: string }) {
    if (args.requestType === 'filtering')
      this.updateFilterCount();
  }

  onGridDataBound() {
    this.grid?.autoFitColumns();
    this.updateFilterCount();
  }

  exportExcel() {
    this.grid?.excelExport({ fileName: 'InventoryOnHandReport.xlsx' });
  }

  // Export the footer sums as numbers, not the grid's formatted strings.
  aggregateQueryCellInfo(args: AggregateQueryCellInfoEventArgs): void {
    if ((args.type !== 'Footer' && args.type !== 'GroupFooter') || !args.style)
      return;

    const style = args.style as StyleWithValue;
    if (style.value !== GRAND_TOTAL_LABEL)
      style.value = Number(style.value.toString().replace(/[^0-9.-]+/g, ''));
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByRow(index: number, row: OnHandRow): string {
    return `${row.LOT_NUMBER}-${row.STOCK_LOCATOR}-${row.ITEM_NUMBER}-${index}`;
  }

  private updateFilterCount() {
    const fields = (this.grid?.filterSettings?.columns ?? []).map(c => c.field as string);
    this.filterCount = new Set(fields).size;
  }

  private toRow(rec: InventoryOnHandReport): OnHandRow {
    return {
      ...rec,
      dateReceived: this.formatDateReceived(rec.DATE_RECEIVED),
      bornOnDate: rec.BORN_ON_DATE ? moment(rec.BORN_ON_DATE).startOf('day').format('M/D/YYYY') : '',
    };
  }

  /** DATE_RECEIVED is either one MM/DD/YYYY date or a "start-end" range. */
  private formatDateReceived(value: string): string {
    if (!value)
      return '';

    const format = (date: string) => moment(date, 'MM/DD/YYYY').format('M/D/YYYY');
    if (!value.includes('-'))
      return format(value);

    const [startDate, endDate] = value.split('-');
    return `${format(startDate)} - ${format(endDate)}`;
  }
}
